import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import { submitWaitlist } from '../../../services/waitlistService';

export default function WaitlistSection() {
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitState({ status: 'loading', message: '' });
    try {
      await submitWaitlist(data);
      setSubmitState({
        status: 'success',
        message: "You're on the list! We'll be in touch soon.",
      });
      reset();
    } catch (err) {
      const detail =
        err.response?.data?.email?.[0] ||
        err.response?.data?.detail ||
        'Something went wrong. Please try again.';
      setSubmitState({ status: 'error', message: detail });
    }
  };

  const isLoading = submitState.status === 'loading';

  return (
    <Box
      id="waitlist"
      component="section"
      aria-label="Waitlist signup"
      sx={{ py: { xs: 8, sm: 12 }, bgcolor: 'background.default' }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'text.primary',
              fontWeight: 700,
            }}
          >
            Get early access
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 480 }}
          >
            Sign up for the waitlist and be the first to know when Syllabex
            launches.
          </Typography>

          <Card sx={{ width: '100%', mt: 2 }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <Stack spacing={2.5}>
                  <TextField
                    label="Name"
                    fullWidth
                    required
                    autoComplete="name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register('name', { required: 'Name is required' })}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Enter a valid email address',
                      },
                    })}
                  />
                  <TextField
                    label="Institution"
                    fullWidth
                    autoComplete="organization"
                    placeholder="e.g. Boston University"
                    {...register('institution')}
                  />
                  <TextField
                    label="Phone"
                    type="tel"
                    fullWidth
                    autoComplete="tel"
                    {...register('phone')}
                  />
                  <TextField
                    label="Comments or inquiries"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Tell us about your use case or ask a question..."
                    {...register('comments')}
                  />

                  {submitState.status === 'success' && (
                    <Alert severity="success">{submitState.message}</Alert>
                  )}
                  {submitState.status === 'error' && (
                    <Alert severity="error">{submitState.message}</Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    endIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    sx={{ py: 1.5 }}
                  >
                    {isLoading ? 'Submitting...' : 'Join the Waitlist'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
